/**
 * Chat wire → domain adapter (AUD-tail-arch A4).
 *
 * `chatStore.sendMessage` used to destructure the chat response envelope and
 * re-assemble a `Message` inline, which put wire shaping inside a store action.
 * That shaping lives here now, as one pure function with no store, no React and
 * no clock of its own.
 *
 * This module is the ONLY reader of the chat response envelope in the frontend.
 * `ChatResponse` is referenced in exactly three places: its declaration in
 * `lib/types.ts`, `chatApi.sendMessage`'s return type in `lib/api.ts`, and here.
 * Everything downstream — `ChatMessage`, `ExportButton`, `ChatHeaderMenu`, the
 * `lib/types` guards — reads the domain `Message`/`MessageMetadata` instead.
 *
 * Deliberately frontend-only. The leaf types of this payload (`ModelResponse`,
 * `EvaluationScore`, `EpistemicLabel`, …) are already shared via
 * `@arbinex/models`; the *envelope* is not, because frontend and control-plane
 * are separately deployed Railway services and that boundary is allowed to skew
 * mid-deploy. Sharing the envelope would put a compile-time lock on the one
 * contract that has to survive being out of step. (Plan D1, decided 2026-08-28.)
 */
import type { ChatResponse, Message } from '../../lib/types'

/**
 * The success payload of a chat send, i.e. the wire envelope this maps from.
 *
 * Exported so tests can type their fixtures against it. A fixture typed `as never`
 * (or `as any`) checks the adapter against itself and stays green when the
 * envelope's real shape changes — which is the failure this module exists to make
 * impossible.
 */
export type ChatResponseData = NonNullable<ChatResponse['data']>

/**
 * Everything the mapping needs that does not come off the wire. Passed in
 * rather than read here, so the function stays pure and the tests need no
 * clock control.
 */
export interface AdaptChatResponseContext {
  conversationId: string
  /** ISO timestamp for the assistant message. The caller owns the clock. */
  createdAt: string
}

export interface AdaptedChatResponse {
  /** The assistant message, ready to store. */
  message: Message
  /** Models the server could not reach, for the store's warning banner. */
  unavailableModels: string[] | null
}

/**
 * Map a successful chat response onto the domain `Message` the store keeps.
 *
 * Pure: same input, same output, no I/O, no `Date.now()`, no `set()`.
 *
 * Note what is NOT here: the `!messageId` version-skew guard — the one that
 * decides to fail the send and surface an outage message — stays in the store.
 * Deciding to fail a send is control flow, not mapping. The `messageId`
 * assertion below is a different thing: a structural precondition, so a caller
 * that skipped the store's guard gets a named error instead of a `Message` with
 * `id: undefined`. It never fires on the store's path.
 *
 * Throws only on that violated precondition; otherwise total.
 */
export function adaptChatResponse(
  data: ChatResponseData,
  { conversationId, createdAt }: AdaptChatResponseContext
): AdaptedChatResponse {
  const {
    messageId,
    bestResponse,
    responses,
    evaluation,
    totalCost,
    requestLatency,
    mode,
    warnings,
    trace,
    metadata,
    requestId
  } = data

  // Structural invariant, NOT the send-failure decision. `chatStore` still owns
  // that: it checks `messageId` first and throws the user-facing "Server did not
  // return a message id", which marks the send failed. This is the backstop for
  // a *different* caller — a future streaming/regenerate/retry path that maps a
  // response without replicating that check. `ChatResponseData` types
  // `messageId` as `string`, so tsc cannot catch such a caller, and without this
  // the adapter would happily return a `Message` with `id: undefined` and
  // reproduce the 2026-08-25 incident on the following send. Refusing to build a
  // structurally invalid `Message` is a mapper's own business.
  if (!messageId) {
    throw new Error('adaptChatResponse: response.data.messageId is required (guard it before mapping)')
  }

  const unavailableModels = warnings?.unavailableModels || null
  const synthesized = metadata?.synthesized // Will be undefined when synthesisStatus is 'pending'
  const synthesisStatus = metadata?.synthesisStatus
  const semanticComparison = metadata?.semanticComparison
  const isPartialResult = metadata?.isPartialResult
  const failedModels = metadata?.failedModels
  const timedOutModels = metadata?.timedOutModels
  const evaluationEnabled = metadata?.evaluationEnabled
  const confidenceLevel = metadata?.confidenceLevel
  const confidence = metadata?.confidence
  const epistemicCoverage = metadata?.epistemicCoverage
  const factCheckResults = metadata?.factCheckResults
  const perModelAlignment = metadata?.perModelAlignment
  const rawEpistemicLabels = metadata?.epistemicLabels
  const epistemicLabels = rawEpistemicLabels && factCheckResults
    ? rawEpistemicLabels.map(label => {
        const matches = factCheckResults.filter(fc => fc.claim === label.claim && fc.url)
        if (matches.length === 0) return label
        const sources = [
          ...(label.sources ?? []),
          ...matches.map(fc => ({ url: fc.url as string, title: fc.source }))
        ]
        return { ...label, sources }
      })
    : rawEpistemicLabels
  const perModelEpistemicCounts = metadata?.perModelEpistemicCounts

  const message: Message = {
    // The server's persisted id, NOT a locally minted one. This read
    // `${currentConversation.id}-${Date.now()}` until 2026-08-25: an id
    // that exists in no table, so every route addressing a message by id
    // 404'd for the message the user had just received. The one that bit
    // was `POST /api/chat/synthesis/on-demand` — "Show merged response"
    // spun for a second and returned to the same button, and only started
    // working after switching conversations, because the reload replaced
    // the fabricated ids with real ones.
    //
    // Note the fabricated id did NOT start with `temp-`, so the
    // `!messageId.startsWith('temp-')` guard in chatStore — which exists
    // for exactly this class of mistake — never saw it.
    id: messageId,
    conversationId,
    role: 'assistant',
    content: bestResponse.content,
    createdAt,
    metadata: {
      mode,
      modelsUsed: responses.map(r => r.model),
      bestModel: bestResponse.model,
      cost: totalCost,
      responses,
      evaluation,
      trace,
      synthesized,
      synthesisStatus,
      ...(requestId && { requestId }),
      semanticComparison,
      ...(isPartialResult && { isPartialResult, failedModels, timedOutModels }),
      ...(evaluationEnabled !== undefined && { evaluationEnabled }),
      ...(confidenceLevel && { confidenceLevel }),
      ...(confidence !== undefined && { confidence }),
      ...(epistemicCoverage !== undefined && { epistemicCoverage }),
      ...(factCheckResults && { factCheckResults }),
      ...(perModelAlignment && { perModelAlignment }),
      ...(epistemicLabels && { epistemicLabels }),
      ...(perModelEpistemicCounts && { perModelEpistemicCounts }),
      // Named explicitly, like every other field here: nothing in this
      // object is spread from the response, so an unlisted field never
      // reaches the message. The server persists the same value, so the
      // reload path picks it up from the database blob.
      ...(requestLatency !== undefined && { requestLatency })
    }
  }

  return { message, unavailableModels }
}
